import React, { useEffect, useMemo, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { yup } from '@reapitform/utils'
import { ButtonGroup, Checkbox, Drawer, InputGroup, Select } from '@reapitform/common-ui'
import { Button } from '@reapit/elements/core/button'
import { Dialog } from '@reapit/elements/core/dialog'
import { TextareaControl } from '@reapit/elements/core/textarea-control'
import { useAdminEnums } from '../../../../api/enums'
import { useAdminMergeFields } from '../../../../api/merge-fields'
import { AdminEventTemplate, EnumOption } from '../../../../api/types'
import { useCreateAdminEventTemplate, useUpdateAdminEventTemplate } from '../../../../api/event-templates'
import { FieldHelp, FieldStack } from '../../../../components/form-details-fields/style'

interface EventTemplateValues {
  activity: string
  method: string
  entityType: string
  addDocumentAttachment: boolean
  addDocumentPDF: boolean
  to: string
  from: string
  subject: string
  body: string
}

const EMAIL = 'Email'
const SMS = 'SMS'

/** The Activity values that can actually fire an event template on a Notice — a Notice only ever reaches
 * Draft (Created/Edited/Duplicated) and Closed (NoticeSent), plus the always-available meta activities.
 * Every other DocumentActivity member is agreement-only (signing, expiry, change requests, etc.) and can
 * never trigger for a Notice — offering it here would create a template that is permanently silent, with
 * no feedback anywhere that it will never fire. */
const NOTICE_ACTIVITIES = new Set([
  'None',
  'Created',
  'Edited',
  'Duplicated',
  'NoticeSent',
  'NoticePrinted',
  'Closed',
  'EmailSent',
  'SMSSent',
  'DocumentTypeChanged',
])

/**
 * Filters a `DocumentActivity` catalog down to the ones that can actually fire on a Notice, with a
 * re-admit escape hatch: if `currentValue` is already set to an unreachable activity (from before this
 * filter existed, or from before the form's family was finalised), it's kept as an extra option so editing
 * shows the real saved value. A non-Notice family (or unset) returns the catalog as-is. Pure — takes the
 * catalog and current value as plain arguments so it's testable without rendering React.
 */
export const buildActivityOptions = <T extends { value: string }>(
  catalog: T[],
  family: string | undefined,
  currentValue: string | null | undefined,
): T[] => {
  if (family !== 'Notice') return catalog

  const allowed = catalog.filter((option) => NOTICE_ACTIVITIES.has(option.value))
  const currentExcluded =
    currentValue && !allowed.some((option) => option.value === currentValue)
      ? catalog.find((option) => option.value === currentValue)
      : undefined
  return [...allowed, ...(currentExcluded ? [currentExcluded] : [])]
}

/** The three token-bearing fields the "Insert a merge field" picker can target. */
type TokenField = 'to' | 'subject' | 'body'

// 'None' is a valid PersonType member and, for an Agreement template, a real pre-existing choice: it sends
// one document-scoped message instead of one per recipient at runtime (see
// EventNotificationHandler.MergeAndSendEventNotifications on the API). For a Notice it's rejected — a
// Notice template that never picked a recipient type would be unable to resolve a recipient at send time —
// so the rejection is scoped to `family === 'Notice'` only, to avoid invalidating existing Agreement
// templates saved with 'None'.
const buildEventTemplateSchema = (family?: string) =>
  yup.object({
    activity: yup.string().required('Choose the activity that triggers this'),
    method: yup.string().required('Choose how it is sent'),
    entityType:
      family === 'Notice'
        ? yup
            .string()
            .required('Choose who this notification goes to')
            .notOneOf(['None'], 'Choose who this notification goes to')
        : yup.string().optional().default(''),
    addDocumentAttachment: yup.boolean().default(false),
    addDocumentPDF: yup.boolean().default(false),
    // Email sends To + Subject + Body; SMS sends To + Body. Match the backend's method/field-consistency rules.
    to: yup
      .string()
      .optional()
      .default('')
      .when('method', {
        is: (m: string) => m === EMAIL || m === SMS,
        then: (schema) => schema.required('Enter a recipient'),
      }),
    from: yup.string().optional().default(''),
    subject: yup
      .string()
      .optional()
      .default('')
      .when('method', { is: EMAIL, then: (schema) => schema.required('Enter a subject') }),
    body: yup
      .string()
      .optional()
      .default('')
      .when('method', {
        is: (m: string) => m === EMAIL || m === SMS,
        then: (schema) => schema.required('Enter a message body'),
      }),
  })

interface EventTemplateDrawerProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  formReferenceID: string
  versionReferenceID: string
  /** The event template being edited, or null to create a new one. */
  eventTemplate: AdminEventTemplate | null
  /** The form's document family ("Agreement" | "Notice") — narrows the Activity choices for a Notice to
   * the ones that can actually fire (see NOTICE_ACTIVITIES). Absent/"Agreement" offers the full catalog. */
  family?: string
  onSaved: () => void
}

/** Add/Edit drawer for an event template (notification). Creates (POST) or updates (PUT) then refreshes. */
export const EventTemplateDrawer: React.FC<EventTemplateDrawerProps> = ({
  isOpen,
  onOpenChange,
  formReferenceID,
  versionReferenceID,
  eventTemplate,
  family,
  onSaved,
}) => {
  const formId = 'event-template-form'
  const { data: enums } = useAdminEnums()
  const { data: mergeFields } = useAdminMergeFields()
  const [createTemplate, creating] = useCreateAdminEventTemplate(formReferenceID, versionReferenceID)
  const [updateTemplate, updating] = useUpdateAdminEventTemplate(
    formReferenceID,
    versionReferenceID,
    eventTemplate?.referenceID ?? '',
  )
  const isBusy = creating || updating
  const [previewOpen, setPreviewOpen] = useState(false)
  const eventTemplateSchema = useMemo(() => buildEventTemplateSchema(family), [family])

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<EventTemplateValues>({
    resolver: yupResolver(eventTemplateSchema),
    defaultValues: {
      activity: '',
      method: '',
      entityType: '',
      addDocumentAttachment: false,
      addDocumentPDF: false,
      to: '',
      from: '',
      subject: '',
      body: '',
    },
  })

  useEffect(() => {
    if (!isOpen) return
    reset({
      activity: eventTemplate?.activity ?? '',
      method: eventTemplate?.method ?? '',
      // A Notice template saved with EntityType 'None' (from before this validation existed, or a
      // hand-authored row) is intentionally NOT passed through here as-is - see the notOneOf check above.
      // Falling back to '' for it too means the Select renders empty rather than a phantom 'None' value, so
      // the admin is prompted to pick a real recipient type before saving again. An Agreement template's
      // 'None' is a real, still-valid document-scoped choice (see buildEventTemplateSchema) and is passed
      // through unchanged.
      entityType:
        family === 'Notice' && eventTemplate?.entityType === 'None'
          ? ''
          : (eventTemplate?.entityType ?? (family === 'Notice' ? '' : 'None')),
      addDocumentAttachment: eventTemplate?.addDocumentAttachment ?? false,
      addDocumentPDF: eventTemplate?.addDocumentPDF ?? false,
      to: eventTemplate?.to ?? '',
      from: eventTemplate?.from ?? '',
      subject: eventTemplate?.subject ?? '',
      body: eventTemplate?.body ?? '',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, eventTemplate])

  const handleClose = () => onOpenChange(false)

  // For a Notice, hide (not warn about) every Activity that can never fire — see NOTICE_ACTIVITIES.
  const activityOptions = useMemo<EnumOption[]>(
    () => buildActivityOptions(enums?.DocumentActivity ?? [], family, eventTemplate?.activity),
    [enums, family, eventTemplate],
  )
  const methodOptions = enums?.CommunicationType ?? []
  const entityTypeOptions = enums?.PersonType ?? []
  const mergeFieldOptions = useMemo<EnumOption[]>(
    () =>
      (mergeFields ?? []).map((mergeField) => ({
        value: mergeField.token,
        label: `${mergeField.category} · ${mergeField.name}`,
      })),
    [mergeFields],
  )

  const method = watch('method')
  const isEmail = method === EMAIL
  const addDocumentAttachment = Boolean(watch('addDocumentAttachment'))
  const addDocumentPDF = Boolean(watch('addDocumentPDF'))

  // Seed content (subject/body/to) is dense with {{tokens}} — the picker below inserts one at the cursor of
  // whichever of these three fields was last focused, same mechanism as the recipient field drawer's single
  // "Default Value" textarea, just generalised across more than one field.
  const toRef = useRef<HTMLInputElement | null>(null)
  const subjectRef = useRef<HTMLInputElement | null>(null)
  const bodyRef = useRef<HTMLTextAreaElement | null>(null)
  const [activeField, setActiveField] = useState<TokenField>('body')

  const mergedRef = <T extends HTMLInputElement | HTMLTextAreaElement>(
    fieldName: 'to' | 'subject' | 'body',
    localRef: React.MutableRefObject<T | null>,
  ) => {
    const { ref: rhfRef, ...rest } = register(fieldName)
    return {
      ...rest,
      ref: (element: T | null) => {
        rhfRef(element)
        localRef.current = element
      },
    }
  }

  const activeRef = activeField === 'to' ? toRef : activeField === 'subject' ? subjectRef : bodyRef

  // Subject unmounts outside Email — if it was the last-focused field when the method changes away from
  // Email, fall back to Body rather than silently inserting into a hidden field's (still-live) form value.
  useEffect(() => {
    if (!isEmail && activeField === 'subject') {
      setActiveField('body')
    }
  }, [isEmail, activeField])

  const onSubmit: SubmitHandler<EventTemplateValues> = async (values) => {
    // Switching Method away from Email leaves From/Subject/attachment flags sitting in form state from
    // whenever Email was last selected — clear them here so an SMS/etc template never saves stale
    // Email-only data it never showed the user a way to remove.
    const submitValues: EventTemplateValues =
      values.method === EMAIL
        ? values
        : { ...values, from: '', subject: '', addDocumentAttachment: false, addDocumentPDF: false }

    try {
      if (eventTemplate) await updateTemplate(submitValues)
      else await createTemplate(submitValues)
      onSaved()
      onOpenChange(false)
    } catch {
      // Errors (incl. the 409 duplicate-slot) surfaced via the hooks' toasts.
    }
  }

  const footer = (
    <ButtonGroup alignment="right">
      <Button size="medium" variant="secondary" disabled={isBusy} onClick={handleClose} type="button">
        Cancel
      </Button>
      <Button size="medium" variant="primary" isBusy={isBusy} disabled={isBusy} type="submit" form={formId}>
        Save
      </Button>
    </ButtonGroup>
  )

  return (
    <>
      <Drawer
        isOpen={isOpen}
        title={eventTemplate ? 'Edit Notification' : 'Add Notification'}
        onClose={handleClose}
        footerItems={footer}
      >
        {isOpen && (
          <FieldStack id={formId} onSubmit={handleSubmit(onSubmit)}>
            <div>
              <Select
                label="Activity *"
                options={activityOptions}
                value={activityOptions.find((option) => option.value === watch('activity')) ?? null}
                getOptionLabel={(option: EnumOption) => option.label}
                getOptionValue={(option: EnumOption) => option.value}
                onChange={(option: EnumOption | null) => setValue('activity', option?.value ?? '')}
                errorMessage={errors.activity?.message}
              />
              <FieldHelp>
                {family === 'Notice'
                  ? 'The document activity that triggers this notification — typically Notice Sent.'
                  : 'The document activity that triggers this notification (e.g. sent for signing, completed).'}
              </FieldHelp>
            </div>

            <Select
              label="Method *"
              options={methodOptions}
              value={methodOptions.find((option) => option.value === watch('method')) ?? null}
              getOptionLabel={(option: EnumOption) => option.label}
              getOptionValue={(option: EnumOption) => option.value}
              onChange={(option: EnumOption | null) => setValue('method', option?.value ?? '')}
              errorMessage={errors.method?.message}
            />

            <div>
              <Select
                label="Recipient Type *"
                options={entityTypeOptions}
                value={entityTypeOptions.find((option) => option.value === watch('entityType')) ?? null}
                getOptionLabel={(option: EnumOption) => option.label}
                getOptionValue={(option: EnumOption) => option.value}
                onChange={(option: EnumOption | null) => setValue('entityType', option?.value ?? '')}
                errorMessage={errors.entityType?.message}
              />
              <FieldHelp>
                Recipient sends one message per recipient on the document; User sends one to the property manager.
              </FieldHelp>
            </div>

            {/* To/Body apply to every method, so they're always visible — only Subject/From/attachments are
              Email-specific. Gating everything behind picking a Method first (as this used to) hid most of
              the entity's fields until that one choice was made, which made the drawer look far emptier than
              it actually is. */}
            <div>
              <InputGroup
                label="To *"
                placeholder="e.g. {{RecipientContactEmail}}"
                {...mergedRef('to', toRef)}
                onFocus={() => setActiveField('to')}
                errorMessage={errors.to?.message}
              />
              <FieldHelp>Usually a merge token that resolves to the recipient&apos;s address at send time.</FieldHelp>
            </div>

            {isEmail && (
              <>
                <InputGroup
                  label="From"
                  placeholder="Optional sender address"
                  {...register('from')}
                  errorMessage={errors.from?.message}
                />
                <InputGroup
                  label="Subject *"
                  {...mergedRef('subject', subjectRef)}
                  onFocus={() => setActiveField('subject')}
                  errorMessage={errors.subject?.message}
                />
              </>
            )}

            <TextareaControl
              label="Body *"
              helpText="Plain text for SMS. Accepts full HTML for a richer email layout."
              fieldSizing="fixed"
              rows={12}
              {...mergedRef('body', bodyRef)}
              onFocus={() => setActiveField('body')}
              errorText={errors.body?.message}
            />

            {isEmail && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="small" variant="secondary" type="button" onClick={() => setPreviewOpen(true)}>
                  Preview email
                </Button>
              </div>
            )}

            <Select
              label="Insert a merge field"
              isClearable
              optionLabelKey="label"
              optionValueKey="value"
              options={mergeFieldOptions}
              value={null}
              getOptionLabel={(option: EnumOption) => option.label}
              getOptionValue={(option: EnumOption) => option.value}
              onChange={(option: EnumOption | null) => {
                if (!option) return
                const element = activeRef.current
                const current = watch(activeField) ?? ''
                const start = element?.selectionStart ?? current.length
                const end = element?.selectionEnd ?? current.length
                const next = current.slice(0, start) + option.value + current.slice(end)
                setValue(activeField, next)

                const cursorPosition = start + option.value.length
                requestAnimationFrame(() => {
                  element?.focus()
                  element?.setSelectionRange(cursorPosition, cursorPosition)
                })
              }}
            />

            {isEmail && (
              <>
                <Checkbox
                  label="Attach the document"
                  checked={addDocumentAttachment}
                  onChange={() => setValue('addDocumentAttachment', !addDocumentAttachment)}
                />
                <Checkbox
                  label="Attach the document PDF"
                  checked={addDocumentPDF}
                  onChange={() => setValue('addDocumentPDF', !addDocumentPDF)}
                />
              </>
            )}
          </FieldStack>
        )}
      </Drawer>

      {/* Email body preview — rendered in a sandboxed iframe so the email's own <style> blocks
          cannot bleed into the admin app's styles. Only mounted when the dialog is open to avoid
          a stale srcdoc when the body field changes while the dialog is closed. */}
      <Dialog size="large" isOpen={previewOpen} onClose={() => setPreviewOpen(false)}>
        <Dialog.Header action={<Dialog.HeaderCloseButton />}>Email preview</Dialog.Header>
        <Dialog.Body>
          <iframe
            title="Email body preview"
            srcDoc={watch('body')}
            style={{ width: '100%', height: '60vh', border: 'none' }}
            sandbox=""
          />
        </Dialog.Body>
        <Dialog.Footer>
          <ButtonGroup alignment="right">
            <Button size="medium" variant="secondary" type="button" onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
          </ButtonGroup>
        </Dialog.Footer>
      </Dialog>
    </>
  )
}
